import * as React from 'react';
import classnames from 'classnames';
import { CoreRadioButton as RadioButton, } from '../internal/WixUiCoreComponents/components/CoreRadioButton';
import { KEYS } from '../../common/keyCodes';
import { DotNavigationDataKeys, DotNavigationDataHooks } from './dataHooks';
import { st, classes } from './DotNavigation.deprecated.st.css';
import { DotNavigationAnimation, DotNavigationTheme, } from './types';
const MAX_SHORT_LIST_LENGTH = 5;
const NUM_OF_NAV_DOTS = 2;
const START_SIZES = [
    "normal" /* Size.Normal */,
    "normal" /* Size.Normal */,
    "normal" /* Size.Normal */,
    "small" /* Size.Small */,
    "tiny" /* Size.Tiny */,
];
const MIDDLE_SIZES = [
    "tiny" /* Size.Tiny */,
    "small" /* Size.Small */,
    "normal" /* Size.Normal */,
    "small" /* Size.Small */,
    "tiny" /* Size.Tiny */,
];
const END_SIZES = [
    "tiny" /* Size.Tiny */,
    "small" /* Size.Small */,
    "normal" /* Size.Normal */,
    "normal" /* Size.Normal */,
    "normal" /* Size.Normal */,
];
const _isNumber = (value) => typeof value === 'number' && !isNaN(value);
const getFiveFromStart = (start) => [
    start,
    start + 1,
    start + 2,
    start + 3,
    start + 4,
];
/**
 * A dot navigation component
 */
export class DotNavigation extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            savedCurrentIndex: 0,
            animation: null,
        };
        this._getDot = ({ checked, size, fakeRadio = false }) => (React.createElement("div", { ...(fakeRadio && { tabIndex: -1, 'aria-hidden': true }), className: classnames(classes.radio) },
            React.createElement("div", { className: classnames(classes.dot, {
                    [classes.small]: size === "small" /* Size.Small */,
                    [classes.tiny]: size === "tiny" /* Size.Tiny */,
                    [classes.bordered]: this.props.showBorder === true,
                    [classes.light]: this.props.theme === DotNavigationTheme.Light,
                    [classes.checked]: checked,
                    [classes.noOpacityTransition]: this.props.noOpacityTransition,
                }), "data-hook": fakeRadio ? undefined : DotNavigationDataHooks.Dot })));
        this._renderFakeRadio = () => this._getDot({ checked: false, size: "tiny" /* Size.Tiny */, fakeRadio: true });
        this._renderDot = ({ key, checked, size, onChange, onKeyDown, ariaLabel, }) => (React.createElement(RadioButton, { key: key, checked: checked, checkedIcon: this._getDot({ checked, size }), uncheckedIcon: this._getDot({ checked: false, size }), onChange: onChange, onKeyDown: onKeyDown, "aria-label": ariaLabel, className: classes.extendedRadioButton }));
        this._callOnSelect = (index) => () => {
            this.props.onSelect(index);
        };
        this._startKeyDownHandler = (event) => (event.key === KEYS.ArrowLeft || event.key === KEYS.ArrowUp) &&
            event.nativeEvent.preventDefault();
        this._endKeyDownHandler = (event) => (event.key === KEYS.ArrowRight || event.key === KEYS.ArrowDown) &&
            event.nativeEvent.preventDefault();
        this._getKeyDownHandler = (value) => value === 0
            ? this._startKeyDownHandler
            : value === this.props.length - 1
                ? this._endKeyDownHandler
                : undefined;
        this._renderList = (values, sizes) => values.map((value, index) => this._renderDot({
            key: value,
            checked: value === this.props.currentIndex,
            size: sizes[index],
            onChange: this._callOnSelect(value),
            onKeyDown: this._getKeyDownHandler(value),
            ariaLabel: this._getAriaLabel(index),
        }));
        this._renderStartList = () => this._renderList(getFiveFromStart(0), START_SIZES);
        this._renderEndList = () => this._renderList(getFiveFromStart(this.props.length - 5), END_SIZES);
        this._renderMiddleList = () => this._renderList(getFiveFromStart(this.props.currentIndex - 2), MIDDLE_SIZES);
        this._renderLongVersion = () => (React.createElement("div", { className: classnames(classes.radioGroup, {
                [classes.back]: this.state.animation === DotNavigationAnimation.Back,
                [classes.forward]: this.state.animation === DotNavigationAnimation.Forward,
                [classes.backNext]: this.state.animation === DotNavigationAnimation.BackNext,
                [classes.forwardNext]: this.state.animation === DotNavigationAnimation.ForwardNext,
            }) },
            this._renderFakeRadio(),
            !_isNumber(this.props.currentIndex) ||
                this.props.currentIndex < MAX_SHORT_LIST_LENGTH - NUM_OF_NAV_DOTS
                ? this._renderStartList()
                : this.props.currentIndex >=
                    this.props.length - (MAX_SHORT_LIST_LENGTH - NUM_OF_NAV_DOTS)
                    ? this._renderEndList()
                    : this._renderMiddleList(),
            this._renderFakeRadio()));
        this._renderShortVersion = () => {
            let shortVersion = [];
            for (let index = 0; index < this.props.length; index++) {
                shortVersion = [
                    ...shortVersion,
                    this._renderDot({
                        key: index,
                        checked: index === this.props.currentIndex,
                        size: "normal" /* Size.Normal */,
                        onChange: this._callOnSelect(index),
                        ariaLabel: this._getAriaLabel(index),
                    }),
                ];
            }
            return shortVersion;
        };
        this._getAriaLabel = (index) => {
            const ariaLabel = this.props['aria-label'];
            if (Array.isArray(ariaLabel)) {
                return index < ariaLabel.length
                    ? ariaLabel[index]
                    : `${DotNavigation.defaulAriaLabel} ${index}`;
            }
            return `${this.props['aria-label']} ${index}`;
        };
        this._getDataAttributes = () => ({
            [DotNavigationDataKeys.ShowBorder]: this.props.showBorder,
            [DotNavigationDataKeys.Theme]: this.props.theme,
            [DotNavigationDataKeys.SavedCurrentIndex]: this.state.savedCurrentIndex,
        });
        this.render = () => {
            const { length, className } = this.props;
            const short = length <= MAX_SHORT_LIST_LENGTH;
            return _isNumber(length) && length > 0 ? (React.createElement("div", { className: st(classes.root, { short }, className), "data-hook": this.props['data-hook'], ...this._getDataAttributes() }, length <= MAX_SHORT_LIST_LENGTH
                ? this._renderShortVersion()
                : this._renderLongVersion())) : null;
        };
    }
}
DotNavigation.displayName = 'DotNavigation';
DotNavigation.defaulAriaLabel = 'Dot Navigation';
DotNavigation.defaultProps = {
    currentIndex: 0,
    length: MAX_SHORT_LIST_LENGTH,
    onSelect(_) { },
    showBorder: false,
    theme: DotNavigationTheme.Dark,
    'aria-label': DotNavigation.defaulAriaLabel,
};
DotNavigation.shouldAnimate = (currentIndex, savedCurrentIndex, length) => (currentIndex > 2 && currentIndex < length - 3) ||
    (currentIndex === 2 && currentIndex < savedCurrentIndex) ||
    (currentIndex === length - 3 && currentIndex > savedCurrentIndex);
DotNavigation.hasCurrentIndexChanged = (currentIndex, savedCurrentIndex, length) => _isNumber(currentIndex) &&
    currentIndex >= 0 &&
    currentIndex < length &&
    currentIndex !== savedCurrentIndex;
DotNavigation.getLongListAnimation = (nextProps, prevState) => {
    const { currentIndex, length } = nextProps;
    const { savedCurrentIndex } = prevState;
    return DotNavigation.hasCurrentIndexChanged(currentIndex, savedCurrentIndex, length)
        ? {
            ...prevState,
            savedCurrentIndex: currentIndex,
            ...(DotNavigation.shouldAnimate(currentIndex, savedCurrentIndex, length) && {
                animation: currentIndex < savedCurrentIndex
                    ? prevState.animation === DotNavigationAnimation.Back
                        ? DotNavigationAnimation.BackNext
                        : DotNavigationAnimation.Back
                    : prevState.animation === DotNavigationAnimation.Forward
                        ? DotNavigationAnimation.ForwardNext
                        : DotNavigationAnimation.Forward,
            }),
        }
        : null;
};
DotNavigation.getDerivedStateFromProps = (nextProps, prevState) => nextProps.length <= MAX_SHORT_LIST_LENGTH
    ? null
    : DotNavigation.getLongListAnimation(nextProps, prevState);
//# sourceMappingURL=DotNavigation.js.map